import { Stack } from "@mui/material";
import { ReactNode, useMemo } from "react";
import { ReuseEllipsisTooltip } from "../ui";
import { RecommendedAccount } from "../../types/apiTypes";
import { TableColumnType } from "../../types/genereal";
import { RecommendedMenuBtn } from "./RecommendedMenuBtn";
import { RecommendedCheckbox } from "./RecommendedCheckbox";

const TextCell = ({ text }: { text: string }) => (
  <ReuseEllipsisTooltip
    tooltip={text}
    children={text}
    style={{
      fontSize: "14px",
      lineHeight: "22px",
      fontWeight: 400,
      color: "#232323",
    }}
  />
);

type RowClient =
  | "name"
  | "industry"
  | "region"
  | "size"
  | "revenue"
  | "menu"
  | "checkbox";

type MapperFn = (row: RecommendedAccount) => ReactNode;

type Props = {
  columns: TableColumnType[];
  row: RecommendedAccount;
  isDemo: boolean;
};

export const RecommendedListRow = ({ columns, row, isDemo }: Props) => {
  const mapper: Record<RowClient, MapperFn> = useMemo(
    () => ({
      name: ({ name }: RecommendedAccount) => <TextCell text={name} />,
      industry: ({ industry }: RecommendedAccount) => (
        <TextCell text={industry} />
      ),
      region: ({ region }: RecommendedAccount) => <TextCell text={region} />,
      size: ({ size }: RecommendedAccount) => <TextCell text={size} />,
      revenue: ({ revenue }: RecommendedAccount) => <TextCell text={revenue} />,

      menu: ({ id, reason }: RecommendedAccount) => (
        <RecommendedMenuBtn id={id} reason={reason} isDisabled={isDemo} />
      ),
      checkbox: ({ id }: RecommendedAccount) => (
        <RecommendedCheckbox id={id} isDisabled={isDemo} />
      ),
    }),
    [isDemo],
  );

  return (
    <Stack
      sx={{
        flexDirection: "row",
        width: "100%",
      }}
    >
      {columns.map(
        ({ key, minWidth, maxWidth, bgColor, textAlign, padding }, index) =>
          index === 0 ? null : (
            <Stack
              key={key}
              sx={{
                minWidth,
                maxWidth,
                borderBottom: bgColor ? "none" : "1px solid #DEDEDE",
                backgroundColor: bgColor,
                p: padding ?? "16px 0 16px 16px",
                width: "100%",
                textAlign: textAlign,
                justifyContent: "center",
              }}
            >
              {mapper[key as RowClient] && mapper[key as RowClient](row)}
            </Stack>
          ),
      )}
    </Stack>
  );
};
